import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import { WS_INVALIDATED_QUERY_STALE_TIME } from '../constants';
import { operations, SchemaTransferDto } from '../generated/apiTypes';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { removeUndefined } from '../lib/objectHelpers';
import { PrettyMerge } from '../lib/tsUtilityTypes';
import { useFlattenedPages } from './useFlattenedPages';

export const transformTransfer = (transfer: SchemaTransferDto) => ({
  ...transfer,
  amount: Gwei.toGwei(transfer.amount),
  fee: Gwei.toGwei(transfer.fee),
});
export type Transfer = ReturnType<typeof transformTransfer>;

export type TransferParams = PrettyMerge<
  operations['TokenController_listTransfers']['parameters']['query'],
  { subaccountId: string | undefined }
>;
export const transferQuery = ({ subaccountId, limit, statuses, ...params }: TransferParams) =>
  infiniteQueryOptions({
    queryKey: ['GET', '/v1/token/transfer', { subaccountId }, statuses, limit, params] as const,
    initialPageParam: '',
    enabled: Boolean(subaccountId),
    queryFn: async ({ queryKey, signal }) => {
      if (!subaccountId) return { data: [], hasNext: false, nextCursor: undefined };
      const [method, url] = queryKey;
      const { data } = await fetchClient[method](url, {
        signal,
        params: { query: removeUndefined({ subaccountId, statuses, limit, ...params }) },
      }).then(dataOrThrow);
      const transfers = data?.data.map(transformTransfer) ?? [];
      return {
        data: transfers,
        hasNext: data?.hasNext || false,
        nextCursor: data?.nextCursor,
      };
    },
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.nextCursor : undefined),
  });

export const useTransfers = ({ subaccountId, statuses, limit, ...params }: TransferParams) => {
  const { data, error, isFetching, isPending, hasNextPage } = useInfiniteQuery(
    transferQuery({ subaccountId, statuses, limit, ...params })
  );
  const flatData = useFlattenedPages('data', data?.pages);
  return { data: flatData, error, isFetching, isPending, hasNextPage };
};
